import { CircleAlert, CircleCheck, CircleDashed, CircleX } from 'lucide-react';

import type {
  ReadinessEvidenceCounts,
  ReadinessEvidenceItem,
  ReadinessEvidenceStatus,
} from '@/lib/api/readiness';

/**
 * The shared vocabulary for readiness evidence: one place that decides what a
 * status is called, what colour it is, and how a set of them is described in
 * words. The card, the drawer and the filter tabs all read from here, so a
 * status can never mean one thing in the preview and another in the drill-down.
 */

/**
 * Worst-first, everywhere. The bar, the chips and the filter tabs all use this
 * order because the whole point of the surface is what is costing the student
 * score — that belongs at the start of the sentence, not the end.
 */
export const EVIDENCE_STATUSES: readonly ReadinessEvidenceStatus[] = [
  'missed',
  'partial',
  'met',
  'neutral',
];

export const STATUS_ICON: Record<ReadinessEvidenceStatus, typeof CircleCheck> = {
  met: CircleCheck,
  missed: CircleX,
  partial: CircleAlert,
  neutral: CircleDashed,
};

export const STATUS_TONE: Record<ReadinessEvidenceStatus, string> = {
  met: 'text-emerald-700',
  missed: 'text-red-700',
  partial: 'text-amber-700',
  neutral: 'text-muted-foreground',
};

/** Segment fill for the distribution bar. */
export const STATUS_FILL: Record<ReadinessEvidenceStatus, string> = {
  met: 'bg-emerald-500',
  missed: 'bg-red-500',
  partial: 'bg-amber-500',
  neutral: 'bg-muted-foreground/30',
};

/** Plain-English status, so the affordance never rests on colour alone. */
export const STATUS_LABEL: Record<ReadinessEvidenceStatus, string> = {
  met: 'Counted in full',
  missed: 'Costing score',
  partial: 'Partial credit',
  neutral: 'No effect',
};

/** The same words as a countable noun phrase: "64 costing score". */
export const STATUS_COUNT_LABEL: Record<ReadinessEvidenceStatus, string> = {
  met: 'counted in full',
  missed: 'costing score',
  partial: 'partial credit',
  neutral: 'no effect',
};

/** Anything unrecognised is treated as inert rather than guessed at. */
export function safeStatus(status: string): ReadinessEvidenceStatus {
  return status in STATUS_ICON ? (status as ReadinessEvidenceStatus) : 'neutral';
}

export function countFor(
  counts: ReadinessEvidenceCounts | undefined,
  status: ReadinessEvidenceStatus,
): number {
  return counts?.[status] ?? 0;
}

/** One drawn segment of the distribution bar. Zero-count statuses are dropped. */
export interface EvidenceSegment {
  status: ReadinessEvidenceStatus;
  count: number;
  /** Percent of the total, 0..100. */
  pct: number;
}

/**
 * The distribution as proportions of the whole set.
 *
 * Reads `counts`, never a list: this is the one part of the UI that has to stay
 * legible whether the component has three records behind it or three hundred,
 * and a list-derived bar would be a lie the moment the API previews.
 */
export function distributionSegments(
  counts: ReadinessEvidenceCounts | undefined,
): EvidenceSegment[] {
  if (!counts || counts.total <= 0) return [];
  return EVIDENCE_STATUSES.map((status) => ({
    status,
    count: countFor(counts, status),
    pct: (countFor(counts, status) / counts.total) * 100,
  })).filter((s) => s.count > 0);
}

/** "327 records — 64 costing score, 1 partial credit, 262 counted in full". */
export function describeDistribution(counts: ReadinessEvidenceCounts | undefined): string {
  const segments = distributionSegments(counts);
  if (segments.length === 0) return 'No records';
  const parts = segments.map((s) => `${s.count} ${STATUS_COUNT_LABEL[s.status]}`);
  return `${counts!.total} record${counts!.total === 1 ? '' : 's'} — ${parts.join(', ')}`;
}

/**
 * Past this many rows a flat list stops being scannable and the repeated module
 * name becomes the loudest thing on screen, so it is promoted to a heading.
 */
export const EVIDENCE_GROUP_THRESHOLD = 8;

export interface EvidenceGroup {
  key: string;
  label: string;
  items: ReadinessEvidenceItem[];
}

/**
 * Group a long list under its `detail` (the module / assessment a record
 * belongs to), so the name is a heading rather than a line repeated on every
 * row.
 *
 * Returns `null` when grouping would not earn its headings — a short list, a
 * list where some records have no detail to group under, or one where every
 * record has a different detail (which would produce a heading per row). The
 * API's worst-first order is preserved both between groups and inside them.
 */
export function groupEvidenceByDetail(
  items: ReadinessEvidenceItem[],
  threshold: number = EVIDENCE_GROUP_THRESHOLD,
): EvidenceGroup[] | null {
  if (items.length <= threshold) return null;

  const order: string[] = [];
  const byDetail = new Map<string, ReadinessEvidenceItem[]>();
  for (const item of items) {
    const key = item.detail?.trim() ?? '';
    // One ungrouped record would need an "Other" bucket, which is a heading
    // that explains nothing. Fall back to the flat list instead.
    if (!key) return null;
    if (!byDetail.has(key)) {
      byDetail.set(key, []);
      order.push(key);
    }
    byDetail.get(key)!.push(item);
  }

  if (order.length < 2 || order.length >= items.length) return null;
  return order.map((key) => ({ key, label: key, items: byDetail.get(key)! }));
}
